import { CSSProperties } from 'react';
import { styled, css } from 'styled-components';

type DayRowProps = {
  $mode?: 'comfortable' | 'compact';
  /** Number of cards in this row, used to size it while it is off screen. See contain-intrinsic-height below. */
  $dayRowCards?: number;
};

export const Container = styled.div`
  background: #fff;
  height: 100%;
  overflow-anchor: none;
  overflow-y: auto;
  overscroll-behavior: contain;
  overflow-x: hidden;
  width: 100%;

  @media print {
    height: auto;
    overflow: visible;
    scrollbar-width: none;
    transform: none !important;
    -ms-overflow-style: none;

    .no-print {
      display: none !important;
    }

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

// $dayRowCards reaches the CSS below as a custom property rather than being interpolated into the
// rule. Interpolating it would make styled-components generate a class per distinct card count and
// inject a new rule whenever a row's count changes as data loads — stylesheet churn is the opposite
// of what content-visibility is here to achieve.
export const DayRow = styled.div.attrs<DayRowProps>(({ $dayRowCards = 1 }) => ({
  style: { '--day-row-cards': $dayRowCards } as CSSProperties,
}))<DayRowProps>`
  ${({ theme: { colors }, $mode }) => css`
    align-items: flex-start;
    display: flex;

    ${
      $mode === 'compact' &&
      css`
        border-bottom: 1px solid ${colors.brandDarkAlpha10};
        padding: 2rem 1rem;

        /* Let the browser skip style, layout and paint for rows that are off screen. The daily view
           keeps every loaded day mounted, so a busy account reaches tens of thousands of nodes and
           any layout read anywhere in the app (the compose box makes many) has to walk all of them.
           Rows stay in the DOM, so the scroll anchoring, the IntersectionObserver and the
           [data-date] queries all behave exactly as before.

           Only compact mode opts in: content-visibility also turns on paint containment, which
           clips anything drawn outside the row. Compact cards are "box-shadow: none" with
           "overflow: hidden", so there is nothing to clip. Comfortable mode uses ListCalendarCard,
           whose 1px outline and hover ActionsArea shadow sit right on the card edge, so it is left
           alone rather than risk a visual change. */
        content-visibility: auto;

        /* Height assumed for rows that have not been displayed yet, so the scrollbar stays about
           right. "auto" means any row that HAS been displayed uses its real remembered height, so
           this estimate only ever applies to rows the user has not reached. --day-row-cards is set
           per row from the message count; 2.75rem is one compact card plus its gap, 4rem the row's
           vertical padding. Width is left alone (contain-intrinsic-width defaults to none). */
        contain-intrinsic-height: auto calc(4rem + (var(--day-row-cards, 1) * 2.75rem));
      `
    }

    ${
      $mode === 'comfortable' &&
      css`
        padding: 1px 1rem 1rem;
      `
    }

    @media print {
      padding: 0.5rem 0;

      /* Printing and the HTML export lay out the whole grid at once, including rows that were never
         scrolled to. Skipping them here would print them blank, and the export copies the
         styled-components stylesheet into the generated file, so this override has to travel with
         it. */
      content-visibility: visible;
      contain-intrinsic-height: none;
    }
  `}
`;

export const DayLabel = styled.div<{ $isToday: boolean }>`
  ${({ theme: { colors, fontSizes, fontWeights }, $isToday }) => css`
    align-items: center;
    cursor: pointer;
    display: flex;
    flex-shrink: 0;
    width: 8rem;
    padding: 0.125rem 0 0 0.25rem;
    position: sticky;
    top: 0;

    @media print {
      position: static;
      top: auto;
    }

    span {
      color: ${$isToday ? colors.brandPrimary : colors.brandDark};
      font-size: 0.625rem;
      letter-spacing: 0.05em;

      b {
        font-size: ${fontSizes.s20};
        font-weight: ${fontWeights.medium};
        margin-right: 0.25rem;
      }
    }
  `}
`;

export const MessageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;

  .holidays {
    margin-top: 0.5rem;
  }

  @media print {
    break-inside: auto;
    page-break-inside: auto;
  }
`;

// As tall as one compact card (2.75rem), so the height assumed for a row that has not been displayed
// yet (see DayRow) is right for an empty date and the list does not jump as it scrolls into view.
export const EmptyDay = styled.div`
  ${({ theme: { colors, fontSizes } }) => css`
    align-items: center;
    box-sizing: border-box;
    color: ${colors.brandDarkAlpha50};
    display: flex;
    font-size: ${fontSizes.s12};
    min-height: 2.75rem;
  `}
`;

export const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5rem 0;

  .heading {
    display: block;
  }
`;
